import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';
import PerformanceChart from './PerformanceChart';

const PlayerProfilePage = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { players, games, gameStats, playersLoading, gamesLoading, getPlayerStats } = useApp();

  const loading = playersLoading || gamesLoading;
  const player = players?.find((p) => p?.id === playerId);
  const stats = useMemo(() => getPlayerStats(playerId), [getPlayerStats, playerId]);

  const playerGameLog = useMemo(() => {
    const gameMap = {};
    games?.forEach((g) => { gameMap[g.id] = g; });
    return (gameStats || [])
      .filter((s) => s?.player_id === playerId)
      .map((s) => {
        const g = gameMap[s?.game_id];
        if (!g) return null;
        const won = g.winning_team === s?.team;
        return {
          id: g.id,
          date: g.date,
          location: g.location,
          won,
          points: s.points || 0,
          rebounds: s.rebounds || 0,
          assists: s.assists || 0,
          steals: s.steals || 0,
          blocks: s.blocks || 0,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const ad = new Date(a.date);
        const bd = new Date(b.date);
        return bd - ad;
      });
  }, [games, gameStats, playerId]);

  const chartData = useMemo(() => {
    // Chart data needs oldest → newest
    return [...playerGameLog].reverse().map((g, idx) => ({
      index: idx + 1,
      date: formatChartDate(g.date),
      points: g.points,
      rebounds: g.rebounds,
      assists: g.assists,
    }));
  }, [playerGameLog]);

  function formatChartDate(dateStr) {
    try {
      const d = dateStr?.includes('/') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  const formatDate = (dateStr) => {
    try {
      const d = dateStr?.includes('/') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr || '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
        <NavigationBar />
        <main className="main-content">
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
          </div>
        </main>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
        <NavigationBar />
        <main className="main-content">
          <div className="px-4 py-20 text-center max-w-md mx-auto">
            <Icon name="AlertCircle" size={40} color="var(--color-muted-foreground)" />
            <h1 className="font-heading font-bold text-xl mt-3" style={{ color: 'var(--color-foreground)' }}>Player Not Found</h1>
            <p className="font-caption text-sm mt-2" style={{ color: 'var(--color-muted-foreground)' }}>
              This player doesn't exist or has been removed.
            </p>
            <Link
              to="/players"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg font-caption text-sm font-medium"
              style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
            >
              <Icon name="ArrowLeft" size={14} color="#fff" />
              Back to Roster
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const hasStats = stats && stats.gamesPlayed > 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-5xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate('/players')}
            className="inline-flex items-center gap-1.5 p-2 -ml-2 mb-2 rounded-lg font-caption text-xs font-medium hover:opacity-80"
            style={{ color: 'var(--color-muted-foreground)' }}
          >
            <Icon name="ArrowLeft" size={14} color="currentColor" />
            Back to Roster
          </button>

          {/* Player header card */}
          <div className="rounded-xl border overflow-hidden mb-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
            <div className="px-4 py-4 sm:px-6 sm:py-5 flex items-center gap-4">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center font-heading font-bold text-2xl sm:text-3xl flex-shrink-0"
                style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}
              >
                {player.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading font-bold text-xl sm:text-2xl truncate" style={{ color: 'var(--color-foreground)' }}>
                    {player.name}
                  </h1>
                  {player.number != null && (
                    <span
                      className="font-data text-sm font-bold px-2 py-0.5 rounded"
                      style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-foreground)' }}
                    >
                      #{player.number}
                    </span>
                  )}
                </div>
                {player.position && (
                  <p className="font-caption text-sm mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>
                    {player.position}
                  </p>
                )}
              </div>
            </div>
          </div>

          {!hasStats ? (
            <div className="rounded-xl border p-8 text-center" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
              <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
              <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>
                {player.name} hasn't played any games yet.
              </p>
            </div>
          ) : (
            <>
              {/* Stat summary grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatCard label="Record" value={`${stats.wins}-${stats.losses}`} sub={`${stats.winPct}% win`} icon="Trophy" />
                <StatCard label="PPG" value={stats.avgPoints} sub={`${stats.gamesPlayed} games`} icon="Target" />
                <StatCard label="RPG" value={stats.avgRebounds} icon="ArrowDownCircle" />
                <StatCard label="APG" value={stats.avgAssists} icon="Zap" />
              </div>

              {/* Performance chart */}
              {chartData.length === 1 && (
                <div className="rounded-xl border overflow-hidden mb-4 px-4 py-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="TrendingUp" size={14} color="var(--color-muted-foreground)" />
                    <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
                      Play 2+ games to see performance trends
                    </p>
                  </div>
                </div>
              )}
              {chartData.length >= 2 && (
                <div className="rounded-xl border overflow-hidden mb-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                  <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>
                      <Icon name="TrendingUp" size={14} color="var(--color-primary)" className="inline mr-2" style={{ verticalAlign: '-2px' }} />
                      Performance Over Time
                    </h3>
                  </div>
                  <div className="px-2 py-3">
                    <PerformanceChart data={chartData} />
                  </div>
                </div>
              )}

              {/* Game log */}
              <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>
                    <Icon name="Clock" size={14} color="var(--color-primary)" className="inline mr-2" style={{ verticalAlign: '-2px' }} />
                    Game Log
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--color-muted)' }}>
                        <th className="px-3 py-2 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Date</th>
                        <th className="px-3 py-2 text-left font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>Result</th>
                        <th className="px-3 py-2 text-right font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>PTS</th>
                        <th className="px-3 py-2 text-right font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>REB</th>
                        <th className="px-3 py-2 text-right font-caption text-xs font-semibold" style={{ color: 'var(--color-muted-foreground)' }}>AST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {playerGameLog.map((g, i) => (
                        <tr key={g.id} style={{ borderTop: i > 0 ? '1px solid var(--color-border)' : undefined }}>
                          <td className="px-3 py-2 font-caption text-xs" style={{ color: 'var(--color-foreground)' }}>{formatDate(g.date)}</td>
                          <td className="px-3 py-2">
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded font-data text-xs font-bold"
                              style={{
                                backgroundColor: g.won ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.08)',
                                color: g.won ? '#16a34a' : '#dc2626',
                              }}
                            >
                              {g.won ? 'W' : 'L'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-data text-sm font-semibold" style={{ color: 'var(--color-foreground)' }}>{g.points}</td>
                          <td className="px-3 py-2 text-right font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{g.rebounds}</td>
                          <td className="px-3 py-2 text-right font-data text-sm" style={{ color: 'var(--color-foreground)' }}>{g.assists}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, sub, icon }) => (
  <div className="rounded-xl border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon name={icon} size={12} color="var(--color-primary)" />
      <span className="font-caption text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-muted-foreground)' }}>{label}</span>
    </div>
    <p className="font-data text-xl font-bold" style={{ color: 'var(--color-foreground)' }}>{value}</p>
    {sub && <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)' }}>{sub}</p>}
  </div>
);

export default PlayerProfilePage;
